<template>
  <div>
    <form class="form-horizontal">
      <div class="row">
        <div class="col-md-6 ts-space-y-3">
          <div class="row">
            <label class="col-md-3 form-col-label control-label">{{
              $t('stockIn.purchaseNumber')
            }}</label>
            <div class="col-md-7">
              <Ponumber
                v-model="model"
                :validate="errors"
                @loading="value => $emit('loading', value)"
              />
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label required">{{
              $t('stockIn.invoiceNumber')
            }}</label>
            <div class="col-md-7">
              <input
                v-model="model.invoice_no"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.has('invoice_no') }"
              />
              <div class="invalid-feedback" v-if="errors.has('invoice_no')">
                {{ errors.first('invoice_no') }}
              </div>
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label required">{{
              $t('stockIn.vendor')
            }}</label>
            <div class="col-md-7">
              <a-select
                v-model="model.vendor_id"
                :options="vendorList"
                showSearch
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                @change="onVendorChange"
                class="ts-w-full"
                :class="{ 'is-invalid': errors.has('vendor_id') }"
              ></a-select>
              <div class="invalid-feedback" v-if="errors.has('vendor_id')">
                {{ errors.first('vendor_id') }}
              </div>
            </div>
          </div>
          <div class="row">
            <label class="form-col-label control-label col-md-3">
              {{ $t('wholesale.creditAmount') }} /
              {{ $t('wholesale.creditDay') }}
            </label>
            <div class="col-md-7">
              <div class="ts-grid ts-grid-cols-2 ts-gap-3">
                <div>
                  <Input
                    :readonly="true"
                    :value="
                      creditAmount
                        ? $defaultCurrencyWithCode(
                            creditAmount,
                            default_precision
                          )
                        : 0
                    "
                    type="text"
                    class="ts-w-full"
                    prefix="ios-cash"
                  />
                </div>
                <div>
                  <Input
                    v-model.number="model.credit_day"
                    type="number"
                    class="ts-w-full"
                    prefix="ios-calendar-outline"
                  />
                  <div class="validate" v-if="errors.has('credit_day')">
                    {{ errors.first('credit_day') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label required">{{
              $t('stockIn.location')
            }}</label>
            <div class="col-md-7">
              <div class="ts-border ts-rounded-md ts-p-2">
                <RadioGroup
                  v-model="model.stock_in_warehouse_type"
                  @on-change="onStockInType"
                >
                  <Radio label="vendor">
                    <span>{{ $t('stockIn.vendor') }}</span>
                  </Radio>
                  <Radio label="customer">
                    <span>{{ $t('stockIn.customer') }}</span>
                  </Radio>
                </RadioGroup>
                <a-select
                  :loading="loading"
                  :disabled="model.stock_in_warehouse_type == 'vendor'"
                  class="ts-w-full"
                  v-model="model.location_id"
                  :options="locationList"
                  showSearch
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  :class="{ 'is-invalid': errors.has('location_id') }"
                ></a-select>
              </div>
              <div class="invalid-feedback" v-if="errors.has('location_id')">
                {{ errors.first('location_id') }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 ts-space-y-3">
          <div class="row">
            <label class="col-md-3 form-col-label control-label required">{{
              $t('stockIn.stockInDate')
            }}</label>
            <div class="col-md-7">
              <a-date-picker
                class="ts-w-full"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                v-model="model.stock_in_date"
                :class="{ 'is-invalid': errors.has('stock_in_date') }"
              ></a-date-picker>
              <div class="invalid-feedback" v-if="errors.has('stock_in_date')">
                {{ errors.first('stock_in_date') }}
              </div>
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label">{{
              $t('stockIn.nextPaymentDate')
            }}</label>
            <div class="col-md-7">
              <a-date-picker
                class="ts-w-full"
                format="DD-MM-YYYY"
                valueFormat="DD-MM-YYYY"
                v-model="model.next_payment_date"
                :class="{
                  'is-invalid': errors.has('next_payment_date')
                }"
              ></a-date-picker>
              <div
                class="invalid-feedback"
                v-if="errors.has('next_payment_date')"
              >
                {{ errors.first('next_payment_date') }}
              </div>
            </div>
          </div>
          <div class="row">
            <label class="form-col-label control-label col-md-3">{{
              $t('stockIn.purchaseType')
            }}</label>
            <div class="col-md-7">
              <div class="ts-flex ts-items-center">
                <a-select
                  v-model="model.purchase_type_id"
                  :options="purchaseTypeList"
                  showSearch
                  :showArrow="false"
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  class="ts-w-full"
                ></a-select>
                <a
                  href="#"
                  @click.prevent="() => (add_new_purchase_type = true)"
                  v-tooltip="$t('add_new')"
                  class="ts-bg-blue-500 ts-h-8 ts-w-8 ts-text-white ts-flex ts-items-center ts-rounded-tr ts-rounded-br hover:ts-text-white"
                  ><i class="fas fa-plus-square ts-mx-auto"></i
                ></a>
              </div>
              <div class="validate" v-if="errors.has('purchase_type_id')">
                {{ errors.first('purchase_type_id') }}
              </div>
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label required">{{
              $t('stockIn.fxkhr')
            }}</label>
            <div class="col-md-3">
              <input
                v-model="model.fx_rate"
                type="text"
                readonly
                class="form-control"
              />
            </div>
            <label class="col-md-1 form-col-label control-label required">{{
              $t('stockIn.fxUSD')
            }}</label>
            <div class="col-md-3">
              <input
                v-model="model.fx_bath"
                type="text"
                readonly
                class="form-control"
              />
            </div>
          </div>
          <div class="row">
            <label class="col-md-3 form-col-label control-label">{{
              $t('stockIn.remark')
            }}</label>
            <div class="col-md-7">
              <textarea
                v-model="model.remarks"
                rows="3"
                class="form-control ts-w-full"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Purchase Type -->
    <a-modal
      :title="$t('purchaseOrder.createPurchaseType')"
      width="30%"
      v-model="add_new_purchase_type"
      :footer="null"
    >
      <div class="row">
        <label class="form-col-label control-label col-md-12 required">{{
          $t('purchaseOrder.purchaseType')
        }}</label>
        <div class="col-md-12">
          <input
            v-model="purchase_type_model.purchase_type"
            type="text"
            class="form-control"
          />
          <div class="validate" v-if="errors.has('purchase_type')">
            {{ errors.first('purchase_type') }}
          </div>
        </div>
      </div>
      <div class="row ts-mt-4">
        <div
          class="col-md-12 ts-flex ts-items-center ts-space-x-3 ts-justify-end"
        >
          <ts-button @click.prevent="() => (add_new_purchase_type = false)">
            {{ $t('cancel') }}
          </ts-button>
          <ts-button
            :waiting="waiting_new_purchase_type"
            @click.prevent="onSavePurchaseType"
            color="primary"
            >{{ $t('save') }}</ts-button
          >
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import Ponumber from './purchase-order-number'
import { mapActions } from 'vuex'

export default {
  name: 'headerForm',
  props: ['value'],
  components: {
    Ponumber
  },
  data() {
    return {
      loading: false,
      errors: new Errors(),
      locations: [],
      add_new_purchase_type: false,
      waiting_new_purchase_type: false,
      model: {},
      purchase_type_model: {
        purchase_type: null
      }
    }
  },
  computed: {
    locationList() {
      return this.locations.map(el => ({
        value: el.location_id,
        label: el.location_name_en
      }))
    },
    vendorList() {
      return this.$store.state.stock.stockIn.suppliers.map(el => ({
        value: el.vendor_id,
        label: el.vendor_name,
        credit_day: el.credit_day,
        credit_amount: el.credit_amount
      }))
    },
    purchaseTypeList() {
      return this.$store.state.stock.stockIn.purchaseTypes.map(el => ({
        value: el.purchase_type_id,
        label: el.purchase_type
      }))
    },
    default_precision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
    },
    creditAmount() {
      let vendor = this.vendorList.find(v => v.value == this.model.vendor_id)
      if (vendor) {
        return vendor.credit_amount
      }
      return 0
    }
  },
  created() {
    this.model = this.value
    this.onFetchViewModel()
  },
  methods: {
    ...mapActions('stock/stockIn', ['getFormViewModel']),
    onSavePurchaseType() {
      this.waiting_new_purchase_type = true
      this.errors = new Errors()
      this.$store
        .dispatch('purchaseOrder/purchaseType/store', this.purchase_type_model)
        .then(async response => {
          this.$toasted.success(response.message)
          await this.$store.commit(
            'stock/stockIn/PUSH_NEW_PURCHASE_TYPE',
            response.data
          )
          this.model.purchase_type_id = response.data.purchase_type_id
          this.purchase_type_model.purchase_type = ''
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new_purchase_type = false
        })
    },
    onVendorChange(value, opt) {
      this.model.credit_day = opt.data.props.credit_day
    },
    onFetchViewModel() {
      this.loading = true
      return this.getFormViewModel({
        fnName: 'location',
        stockType: this.model.stock_in_warehouse_type
      })
        .then(({ data }) => {
          this.locations = data.location
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onStockInType() {
      this.model.location_id = ''
      this.location = []
      this.onFetchViewModel().then(() => {
        if (this.model.stock_in_warehouse_type == 'vendor') {
          this.model.location_id = this.$store.state.authUser.user.warehouses
            .filter(w => w.pivot.is_default == true)
            .reduce((i, v) => (i = v.location_id), null)
        } else {
          this.model.location_id = ''
        }
      })
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>
